// SportsKioskLivePage — K8 P2.5 P4: every ball machine in use right now, across
// all courts, on one board.
//
// The super admin's answer to one question: "what is running, and is any of it
// in trouble?" It is a dashboard, not a management page — the fleet table stays
// on /devices and one phone's facts and commands stay on /devices/:id; every
// card here links there. Attention first: past its end and paused sort to the
// top, then the ones ending soonest.
//
// Data: GET /admin/kiosk/summary — the same call the Management App dashboard
// makes — refreshed every 30 s; the cards tick every second on their own.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Radio, RefreshCw } from 'lucide-react';
import { SuperAdminShell } from './SportsSuperAdminPage';
import { getSummary, isAdminDenied, errMsg } from '../kiosk/kioskApi';
import { GREEN, GREY, AMBER, AdminDeniedNotice } from '../kiosk/KioskDialogs';
import { useAutoRefresh } from '../kiosk/useAutoRefresh';
import { LiveCard, liveState } from '../kiosk/KioskLive';
import { ensureLiveKeyframes } from '../liveUi';

const card = { background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: '14px 16px' };
const RED = '#FF2D55';

// Sort key: what deserves a look first. Past its end, then paused, then the
// ones ending soonest; open time last because it has no end to worry about.
function attention(s) {
  if (!s) return 9;
  if (s.overrun) return 0;
  if (s.stale) return 1;
  if (s.fixed) return 2;
  return 3;
}

function Pill({ label, value, color, testId }) {
  return (
    <div style={{ ...card, padding: '10px 14px', minWidth: 110 }} data-testid={testId}>
      <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 2, color: '#555', fontWeight: 700 }}>{label}</p>
      <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, fontWeight: 800, color, lineHeight: 1.1, marginTop: 2 }}>{value}</p>
    </div>
  );
}

export default function SportsKioskLivePage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [at, setAt] = useState(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setBusy(true);
    try {
      setData(await getSummary());
      setErr(null);
      setAt(new Date());
    } catch (e) {
      if (isAdminDenied(e)) setDenied(true); else setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { ensureLiveKeyframes(); load(); }, [load]);
  useAutoRefresh(() => load(true));

  // Sorted once per refresh. The cards keep their own one-second clocks.
  const rows = useMemo(() => {
    const now = Date.now();
    return (data?.live || [])
      .map(lv => ({ lv, s: liveState(lv, now) }))
      .filter(x => x.s)
      .sort((a, b) => attention(a.s) - attention(b.s) || ((a.s.leftMs ?? Infinity) - (b.s.leftMs ?? Infinity)));
  }, [data]);

  const counts = useMemo(() => {
    const c = { inUse: rows.length, over: 0, paused: 0, endingSoon: 0 };
    for (const { s } of rows) {
      if (s.overrun) c.over += 1;
      else if (s.stale) c.paused += 1;
      else if (s.fixed && s.minsLeft <= 10) c.endingSoon += 1;
    }
    return c;
  }, [rows]);

  const fleet = data?.fleet || {};
  const idle = Math.max(0, (fleet.online || 0) - (fleet.in_use || 0));

  const headerRight = (
    <div className="flex items-center gap-3">
      <span style={{ fontSize: 11, color: GREY }} data-testid="kiosk-live-refreshed">
        {at ? `Updated ${at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}` : ''} · refreshes every 30 s
      </span>
      <button type="button" onClick={() => load()} disabled={busy} data-testid="kiosk-live-refresh"
        style={{ background: 'none', border: '1px solid #2A2A2A', borderRadius: 8, padding: 7, color: GREY, cursor: 'pointer' }} aria-label="Refresh now" title="Refresh now">
        <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );

  return (
    <SuperAdminShell title="Live" active="live" headerRight={headerRight}>
      {denied ? <AdminDeniedNotice /> : (
        <div data-testid="kiosk-live-board">
          <p style={{ fontSize: 13, color: GREY, marginBottom: 16 }}>
            <Radio className="h-3.5 w-3.5 inline mr-1" style={{ verticalAlign: -2, color: AMBER }} />
            Every machine in use right now, across all courts. Figures are expected, not earned — the phone&apos;s final record decides.
          </p>

          <div className="flex gap-3 flex-wrap mb-6">
            <Pill label="In use" value={counts.inUse} color={AMBER} testId="kiosk-live-pill-in-use" />
            <Pill label="Past its end" value={counts.over} color={counts.over > 0 ? RED : '#fff'} testId="kiosk-live-pill-over" />
            <Pill label="Paused" value={counts.paused} color={counts.paused > 0 ? GREY : '#fff'} testId="kiosk-live-pill-paused" />
            <Pill label="Ending soon" value={counts.endingSoon} color={counts.endingSoon > 0 ? RED : '#fff'} testId="kiosk-live-pill-ending" />
            <Pill label="Idle" value={idle} color={GREEN} testId="kiosk-live-pill-idle" />
            <Pill label="Offline" value={fleet.offline || 0} color={(fleet.offline || 0) > 0 ? RED : '#fff'} testId="kiosk-live-pill-offline" />
          </div>

          {err && (
            <div style={{ ...card, borderColor: `${RED}55`, marginBottom: 16 }} data-testid="kiosk-live-error">
              <p style={{ fontSize: 13, color: RED, fontWeight: 700 }}>The board could not be refreshed.</p>
              <p style={{ fontSize: 12, color: GREY, marginTop: 4 }}>{err} <button type="button" onClick={() => load()} style={{ color: '#fff', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>Try again</button></p>
            </div>
          )}

          {!data && !err && (
            <div style={{ ...card, color: GREY, fontSize: 13 }} data-testid="kiosk-live-loading">Loading the fleet…</div>
          )}

          {data && rows.length === 0 && !err && (
            <div style={{ ...card, textAlign: 'center', padding: '32px 16px' }} data-testid="kiosk-live-empty">
              <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 20, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>No machine in use right now</p>
              <p style={{ fontSize: 13, color: GREY, marginTop: 6 }}>
                {idle} online and idle · {fleet.offline || 0} offline · {fleet.devices || 0} in the fleet
              </p>
              <Link to="/sports/super-admin/devices" style={{ display: 'inline-block', marginTop: 12, fontSize: 13, color: AMBER, textDecoration: 'none' }} data-testid="kiosk-live-empty-fleet-link">
                View the fleet →
              </Link>
            </div>
          )}

          {rows.length > 0 && (
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}>
              {rows.map(({ lv }) => (
                <Link key={lv.kiosk_id} to={`/sports/super-admin/devices/${lv.kiosk_id}`}
                  style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
                  data-testid={`kiosk-live-card-${lv.kiosk_id}`} title="Open this phone">
                  <LiveCard
                    live={lv}
                    audience="owner"
                    deviceName={lv.name}
                    siteName={[lv.court_name, lv.site_name].filter(Boolean).join(' · ')}
                    testId={`kiosk-live-${lv.kiosk_id}`}
                  />
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </SuperAdminShell>
  );
}
